import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Download, Lock } from 'lucide-react';
import Shell from '../components/TopBarShell';
import CopyButton from '../components/CopyButton';
import { useLocale } from '../contexts/LocaleProvider';
import { LT } from '../contexts/AppCatalogueContext';
import { deptLabel } from '../lib/departments';
import {
  AI_SURVEY, LEVELS, UI, aiLevel, allQuestions, cleanAnswers, isVisible, optionLabel,
} from '../lib/surveys/aiAtWork';
import api from '../utils/api';

/* Internal survey · one page per survey slug. Everyone fills in the form;
 * HR and app admins also get a Results tab (/surveys/:slug/results).
 * Questions and copy live in lib/surveys/ · see aiAtWork.js. */

const SURVEYS = { [AI_SURVEY.slug]: AI_SURVEY };

const tr = (obj, locale) => (obj ? obj[locale] || obj.fr : '');

export default function Survey() {
  const { slug, view } = useParams();
  const survey = SURVEYS[slug];
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [state, setState] = useState(null);   // GET /me payload
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!survey) return;
    api.surveyMe(slug).then(setState).catch(() => setLoadError(true));
  }, [slug, survey]);

  if (!survey) {
    return <Shell><p style={{ color: LT.green50 }}>404</p></Shell>;
  }

  const showResults = view === 'results' && state?.can_view_results;
  const base = `/surveys/${slug}`;

  return (
    <Shell>
      <div className="max-w-[760px] mx-auto pb-16">
        <header>
          <div className="text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: LT.green50 }}>
            {tr(survey.intro.eyebrow, locale)}
          </div>
          <h1 className="font-display text-[2rem] sm:text-[2.4rem] leading-tight tracking-[-0.02em] mt-2"
              style={{ color: LT.greenDeep }}>
            {tr(survey.intro.title, locale)}
          </h1>
          <p className="text-[15px] leading-relaxed mt-3" style={{ color: '#3a4f44' }}>
            {tr(survey.intro.lede, locale)}
          </p>
        </header>

        {state?.can_view_results && (
          <div className="flex gap-1 mt-6 border-b" style={{ borderColor: LT.green30 }} role="tablist">
            {[['form', base, UI.tabForm], ['results', `${base}/results`, UI.tabResults]].map(([key, to, label]) => {
              const active = key === 'results' ? showResults : !showResults;
              return (
                <button key={key} type="button" role="tab" aria-selected={active}
                        onClick={() => navigate(to)}
                        className="px-3.5 py-2.5 text-[14px] font-medium -mb-px border-b-2 transition"
                        style={{ color: active ? LT.greenDeep : LT.green50, borderColor: active ? LT.greenDeep : 'transparent' }}>
                  {tr(label, locale)}
                </button>
              );
            })}
          </div>
        )}

        {loadError && <p className="mt-8 text-[14px]" style={{ color: '#b23a2a' }}>{tr(UI.failed, locale)}</p>}
        {!state && !loadError && <p className="mt-8 text-[14px]" style={{ color: LT.green50 }}>…</p>}
        {state && (showResults
          ? <Results survey={survey} locale={locale} />
          : <SurveyForm survey={survey} state={state} setState={setState} locale={locale} />)}
      </div>
    </Shell>
  );
}

/* ── Form ─────────────────────────────────────────────────────────── */

function SurveyForm({ survey, state, setState, locale }) {
  const [answers, setAnswers] = useState(() => toFormAnswers(survey, state.response?.answers));
  const [status, setStatus] = useState(state.response ? ['muted', UI.loaded] : null);
  const [saving, setSaving] = useState(false);
  const closed = !state.open;

  const set = (id, value) => setAnswers(a => ({ ...a, [id]: value }));
  const toggle = (id, value, max) => setAnswers(a => {
    const cur = a[id] || [];
    if (cur.includes(value)) return { ...a, [id]: cur.filter(v => v !== value) };
    if (max && cur.length >= max) return a;
    return { ...a, [id]: [...cur, value] };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!answers.freq) { setStatus(['err', UI.needFreq]); return; }
    setSaving(true); setStatus(['muted', UI.saving]);
    try {
      setState(await api.saveSurvey(survey.slug, cleanAnswers(survey, answers), locale));
      setStatus(['ok', UI.saved]);
    } catch {
      setStatus(['err', UI.failed]);
    } finally {
      setSaving(false);
    }
  };

  let n = 0;
  return (
    <form onSubmit={submit} noValidate className="mt-6">
      <div className="rounded-xl px-4 py-3 text-[13.5px] leading-relaxed"
           style={{ background: LT.yellowLight, color: LT.greenDeep }}>
        {tr(UI.answeringAs, locale)} <b>{state.me.full_name}</b>
        {state.me.departement && <> · {deptLabel(state.me.departement, locale)}</>}.{' '}
        {tr(survey.intro.privacy, locale)}
      </div>

      {closed && (
        <div className="mt-4 flex items-center gap-2 text-[14px]" style={{ color: LT.greenDeep }}>
          <Lock className="h-4 w-4" /> {tr(UI.closed, locale)}
        </div>
      )}

      <fieldset disabled={closed} className="m-0 p-0 border-0 min-w-0">
        {survey.sections.map(section => (
          <section key={section.title.en}>
            <h2 className="text-[11px] uppercase tracking-[0.16em] font-medium mt-8 mb-3" style={{ color: LT.green50 }}>
              {tr(section.title, locale)}
            </h2>
            <div className="space-y-3">
              {section.questions.filter(q => isVisible(q, answers)).map(q => (
                <Question key={q.id} q={q} n={++n} locale={locale} answers={answers} set={set} toggle={toggle} />
              ))}
            </div>
          </section>
        ))}
      </fieldset>

      {!closed && (
        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <button type="submit" disabled={saving}
                  className="rounded-xl px-6 py-3 text-[15px] font-medium transition disabled:opacity-50"
                  style={{ background: LT.greenDeep, color: LT.beigeLight }}>
            {tr(state.response ? UI.update : UI.submit, locale)}
          </button>
          {status && (
            <span role="status" className="text-[14px]"
                  style={{ color: status[0] === 'err' ? '#b23a2a' : status[0] === 'ok' ? '#2f7a4f' : LT.green50 }}>
              {tr(status[1], locale)}
            </span>
          )}
        </div>
      )}
    </form>
  );
}

/* Saved answers → form state · an `_other` text means "Other…" was ticked. */
function toFormAnswers(survey, saved) {
  const a = { ...(saved || {}) };
  for (const q of allQuestions(survey)) {
    if (!q.other || a[`${q.id}_other`] == null) continue;
    if (q.type === 'multi') a[q.id] = [...(a[q.id] || []), '__other'];
    else a[q.id] = '__other';
  }
  return a;
}

function Question({ q, n, locale, answers, set, toggle }) {
  const value = answers[q.id];
  const inputId = `q-${q.id}`;
  const label = (
    <span className="flex gap-2.5 text-[15.5px] font-medium leading-snug" style={{ color: LT.greenDeep }}>
      <span className="font-num text-[12.5px] pt-0.5 shrink-0" style={{ color: LT.green50 }}>
        {String(n).padStart(2, '0')}
      </span>
      <span>{tr(q.q, locale)}</span>
    </span>
  );
  const hint = q.hint && (
    <p className="text-[13.5px] mt-1 sm:ml-7" style={{ color: LT.green50 }}>{tr(q.hint, locale)}</p>
  );
  const card = 'rounded-2xl p-4 sm:p-5 m-0 min-w-0';
  const cardStyle = { background: LT.beigeLight, border: `1px solid ${LT.green30}` };

  if (q.type === 'text') {
    return (
      <div className={card} style={cardStyle}>
        <label htmlFor={inputId}>{label}</label>
        {hint}
        <textarea id={inputId} rows={3} value={value || ''} onChange={e => set(q.id, e.target.value)}
                  className="mt-3 sm:ml-7 sm:w-[calc(100%-1.75rem)] w-full rounded-lg px-3 py-2.5 text-[14.5px] resize-y outline-none focus:ring-2"
                  style={{ background: '#fff', border: `1px solid ${LT.green30}`, color: LT.greenDeep, '--tw-ring-color': LT.greenMid }} />
      </div>
    );
  }

  if (q.type === 'matrix') {
    const grid = value || {};
    return (
      <fieldset className={card} style={cardStyle}>
        <legend className="float-left w-full">{label}</legend>
        <div className="clear-both" />
        {hint}
        <div className="mt-3 sm:ml-7 divide-y" style={{ borderColor: LT.green30 }}>
          {q.rows.map(([rid, rlabel]) => (
            <div key={rid} role="radiogroup" aria-label={tr(rlabel, locale)} className="py-2.5 first:pt-0">
              <div className="text-[14px] mb-1.5" style={{ color: '#3a4f44' }}>{tr(rlabel, locale)}</div>
              <div className="flex flex-wrap gap-1.5">
                {q.levels.map(([lid, llabel]) => {
                  const on = grid[rid] === lid;
                  return (
                    <label key={lid} className="relative cursor-pointer">
                      <input type="radio" name={`${q.id}-${rid}`} value={lid} checked={on}
                             onChange={() => set(q.id, { ...grid, [rid]: lid })}
                             className="peer absolute inset-0 opacity-0 cursor-pointer" />
                      <span className="inline-block rounded-full border text-[12.5px] px-2.5 py-1 transition peer-focus-visible:ring-2"
                            style={on
                              ? { background: LT.greenDeep, borderColor: LT.greenDeep, color: LT.beigeLight, '--tw-ring-color': LT.greenMid }
                              : { background: '#fff', borderColor: LT.green30, color: LT.greenDeep, '--tw-ring-color': LT.greenMid }}>
                        {tr(llabel, locale)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </fieldset>
    );
  }

  const multi = q.type === 'multi';
  const opts = q.type === 'scale'
    ? [1, 2, 3, 4, 5].map(v => [String(v), null])
    : [...q.opts, ...(q.other ? [['__other', UI.other]] : [])];
  const isOn = (id) => (multi ? (value || []).includes(id) : value === id);
  const pickedOther = isOn('__other');
  const full = multi && q.max && (value || []).length >= q.max;

  return (
    <fieldset className={card} style={cardStyle}>
      <legend className="float-left w-full">{label}</legend>
      <div className="clear-both" />
      {hint}
      <div className="mt-3 sm:ml-7 flex flex-wrap gap-2 items-center">
        {q.type === 'scale' && <span className="text-[12.5px] mr-1" style={{ color: LT.green50 }}>{tr(q.low, locale)}</span>}
        {opts.map(([id, lbl]) => (
          <label key={id} className="relative cursor-pointer">
            <input type={multi ? 'checkbox' : 'radio'} name={q.id} value={id} checked={isOn(id)}
                   onChange={() => (multi ? toggle(q.id, id, q.max) : set(q.id, id))}
                   disabled={full && !isOn(id)}
                   className="peer absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed" />
            <span className={`inline-block rounded-full border text-[14px] transition peer-focus-visible:ring-2 peer-disabled:opacity-40 ${q.type === 'scale' ? 'w-10 text-center py-1.5' : 'px-3.5 py-1.5'}`}
                  style={isOn(id)
                    ? { background: LT.greenDeep, borderColor: LT.greenDeep, color: LT.beigeLight, '--tw-ring-color': LT.greenMid }
                    : { background: '#fff', borderColor: LT.green30, color: LT.greenDeep, '--tw-ring-color': LT.greenMid }}>
              {lbl ? tr(lbl, locale) : id}
            </span>
          </label>
        ))}
        {q.type === 'scale' && <span className="text-[12.5px] ml-1" style={{ color: LT.green50 }}>{tr(q.high, locale)}</span>}
      </div>
      {pickedOther && (
        <input type="text" value={answers[`${q.id}_other`] || ''} placeholder={tr(UI.otherPh, locale)}
               onChange={e => set(`${q.id}_other`, e.target.value)} autoFocus
               className="mt-3 sm:ml-7 sm:w-[calc(100%-1.75rem)] w-full rounded-lg px-3 py-2 text-[14.5px] outline-none focus:ring-2"
               style={{ background: '#fff', border: `1px solid ${LT.green30}`, color: LT.greenDeep, '--tw-ring-color': LT.greenMid }} />
      )}
    </fieldset>
  );
}

/* ── Results (HR / admins) ────────────────────────────────────────── */

function Results({ survey, locale }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [dept, setDept] = useState('');
  const [freq, setFreq] = useState('');
  const [level, setLevel] = useState('');

  useEffect(() => {
    api.surveyResults(survey.slug).then(setData).catch(() => setError(true));
  }, [survey.slug]);

  // Level is computed once per response · same rules as the later analysis.
  const scored = useMemo(
    () => (data?.responses || []).map(r => ({ ...r, ai: aiLevel(r.answers) })),
    [data],
  );
  const base = useMemo(
    () => scored.filter(r => (!dept || r.departement === dept) && (!freq || r.answers.freq === freq)),
    [scored, dept, freq],
  );
  const rows = useMemo(() => base.filter(r => !level || r.ai.level === level), [base, level]);
  const depts = useMemo(
    () => [...new Set(scored.map(r => r.departement).filter(Boolean))].sort(),
    [scored],
  );

  if (error) return <p className="mt-8 text-[14px]" style={{ color: '#b23a2a' }}>{tr(UI.failed, locale)}</p>;
  if (!data) return <p className="mt-8 text-[14px]" style={{ color: LT.green50 }}>…</p>;

  const qs = allQuestions(survey);
  const count = (fn) => rows.filter(r => fn(r.answers)).length;
  const kpis = [
    [`${data.responses.length}/${data.audience}`, `${tr(UI.responses, locale)} ${tr(UI.ofTeam, locale)}`],
    [count(a => a.freq === 'daily' || a.freq === 'weekly'), tr(UI.weekly, locale)],
    [count(a => a.freq === 'never'), tr(UI.never, locale)],
    [count(a => a.training === 'yes'), tr(UI.wantTraining, locale)],
    [count(a => a.demo === 'yes'), tr(UI.demos, locale)],
  ];
  const risky = rows.filter(r => r.ai.risk).length;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={dept} onChange={e => setDept(e.target.value)}
                className="rounded-lg px-3 py-2 text-[14px]"
                style={{ background: '#fff', border: `1px solid ${LT.green30}`, color: LT.greenDeep }}>
          <option value="">{tr(UI.allTeams, locale)}</option>
          {depts.map(d => <option key={d} value={d}>{deptLabel(d, locale)}</option>)}
        </select>
        <select value={freq} onChange={e => setFreq(e.target.value)}
                className="rounded-lg px-3 py-2 text-[14px]"
                style={{ background: '#fff', border: `1px solid ${LT.green30}`, color: LT.greenDeep }}>
          <option value="">{tr(UI.allFreq, locale)}</option>
          {qs.find(q => q.id === 'freq').opts.map(([id, l]) => <option key={id} value={id}>{tr(l, locale)}</option>)}
        </select>
        <CopyButton value={`${window.location.origin}/surveys/${survey.slug}`} label={tr(UI.copyLink, locale)} />
        <button type="button" onClick={() => downloadCsv(survey, rows, locale)}
                className="inline-flex items-center gap-1.5 text-[13px] px-3 py-2 rounded-lg"
                style={{ color: LT.greenDeep, border: `1px solid ${LT.green30}` }}>
          <Download className="h-3.5 w-3.5" /> {tr(UI.csv, locale)}
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-4">
        {kpis.map(([v, l]) => (
          <div key={l}>
            <div className="font-display text-[1.9rem] leading-none font-num" style={{ color: LT.greenDeep }}>{v}</div>
            <div className="text-[12.5px] mt-1" style={{ color: LT.green50 }}>{l}</div>
          </div>
        ))}
      </div>

      {base.length > 0 && (
        <div className="mt-8">
          <h2 className="text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: LT.green50 }}>
            {tr(UI.levelTitle, locale)}
          </h2>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {LEVELS.map(([id, min, label], i) => {
              const n = base.filter(r => r.ai.level === id).length;
              const on = level === id;
              const max = LEVELS[i + 1] ? LEVELS[i + 1][1] - 1 : 100;
              return (
                <button key={id} type="button" onClick={() => setLevel(on ? '' : id)} aria-pressed={on}
                        className="rounded-xl px-3 py-2.5 text-left transition"
                        style={on
                          ? { background: LT.greenDeep, color: LT.beigeLight, border: `1px solid ${LT.greenDeep}` }
                          : { background: LT.beigeLight, color: LT.greenDeep, border: `1px solid ${LT.green30}` }}>
                  <div className="font-display text-[1.5rem] leading-none font-num">{n}</div>
                  <div className="text-[13px] mt-1 font-medium">{tr(label, locale)}</div>
                  <div className="text-[11.5px] font-num" style={{ opacity: 0.65 }}>{min}–{max}</div>
                </button>
              );
            })}
          </div>
          <p className="text-[12.5px] mt-2" style={{ color: LT.green50 }}>{tr(UI.levelNote, locale)}</p>
          {risky > 0 && (
            <p className="text-[13px] mt-2 inline-flex items-center gap-1.5" style={{ color: '#9a5b0f' }}>
              <AlertTriangle className="h-3.5 w-3.5" /> {risky} · {tr(UI.risk, locale)}
            </p>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="mt-8 text-[14px]" style={{ color: LT.green50 }}>{tr(UI.noAnswers, locale)}</p>
      ) : (
        <>
          {qs.filter(q => q.type !== 'text').map(q => (q.type === 'matrix'
            ? <MatrixTally key={q.id} q={q} rows={rows} locale={locale} />
            : <Tally key={q.id} q={q} rows={rows} locale={locale} />))}

          <h2 className="text-[11px] uppercase tracking-[0.16em] font-medium mt-10 mb-1" style={{ color: LT.green50 }}>
            {tr(UI.textAnswers, locale)}
          </h2>
          {qs.filter(q => q.type === 'text' || q.other).map(q => {
            const key = q.type === 'text' ? q.id : `${q.id}_other`;
            const items = rows.filter(r => (r.answers[key] || '').trim());
            if (!items.length) return null;
            return (
              <div key={key} className="mt-5">
                <h3 className="text-[14.5px] font-medium" style={{ color: LT.greenDeep }}>
                  {tr(q.q, locale)}{q.type !== 'text' && <> · {tr(UI.other, locale)}</>}
                </h3>
                <ul className="mt-2 space-y-2 list-none p-0">
                  {items.map(r => (
                    <li key={r.email} className="rounded-xl px-4 py-3 text-[14px] leading-relaxed"
                        style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}`, color: '#3a4f44' }}>
                      <div className="whitespace-pre-line [overflow-wrap:anywhere]">{r.answers[key]}</div>
                      <div className="text-[12px] mt-1.5" style={{ color: LT.green50 }}>
                        {r.full_name}{r.departement && ` · ${deptLabel(r.departement, locale)}`}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          <h2 className="text-[11px] uppercase tracking-[0.16em] font-medium mt-10 mb-2" style={{ color: LT.green50 }}>
            {tr(UI.people, locale)}
          </h2>
          {rows.map(r => <Person key={r.email} r={r} survey={survey} locale={locale} />)}
        </>
      )}

      {!dept && !freq && !level && data.pending.length > 0 && (
        <>
          <h2 className="text-[11px] uppercase tracking-[0.16em] font-medium mt-10 mb-2" style={{ color: LT.green50 }}>
            {tr(UI.pending, locale)} · {data.pending.length}
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {data.pending.map(p => (
              <span key={p.full_name} className="text-[12.5px] rounded-full px-2.5 py-1"
                    style={{ background: LT.beigeWarm, color: LT.greenDeep }}>
                {p.full_name}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Tally({ q, rows, locale }) {
  const counts = new Map();
  let answered = 0;
  for (const r of rows) {
    const v = r.answers[q.id];
    const vals = Array.isArray(v) ? v : v ? [v] : [];
    const withOther = q.other && r.answers[`${q.id}_other`] && !vals.includes('__other') ? [...vals, '__other'] : vals;
    if (withOther.length) answered += 1;
    withOther.forEach(x => counts.set(x, (counts.get(x) || 0) + 1));
  }
  if (!answered) return null;
  const order = q.type === 'scale' ? ['1', '2', '3', '4', '5'] : [...(q.opts || []).map(([id]) => id), '__other'];
  const entries = order.filter(id => counts.has(id)).map(id => [id, counts.get(id)]);
  if (q.type !== 'scale' && q.type !== 'single') entries.sort((a, b) => b[1] - a[1]);

  return (
    <div className="mt-7">
      <h3 className="text-[14.5px] font-medium" style={{ color: LT.greenDeep }}>
        {tr(q.q, locale)}
        <span className="font-normal text-[12.5px] ml-2" style={{ color: LT.green50 }}>{answered}</span>
      </h3>
      <div className="mt-2 space-y-1.5">
        {entries.map(([id, c]) => (
          <div key={id} className="grid items-center gap-3 text-[13.5px]"
               style={{ gridTemplateColumns: 'minmax(0, 15rem) 1fr 2.25rem' }}>
            <span className="truncate" title={optionLabel(q, id, locale)} style={{ color: '#3a4f44' }}>
              {optionLabel(q, id, locale)}
            </span>
            <span className="h-2.5 rounded-full overflow-hidden" style={{ background: LT.beigeWarm }}>
              <span className="block h-full rounded-full" style={{ width: `${(c / answered) * 100}%`, background: LT.greenMid }} />
            </span>
            <span className="text-right font-num text-[12.5px]" style={{ color: LT.green50 }}>{c}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* One stacked bar per row · darker = further along (never heard → regular). */
function MatrixTally({ q, rows, locale }) {
  const answered = rows.filter(r => Object.keys(r.answers[q.id] || {}).length);
  if (!answered.length) return null;
  const shades = [LT.beigeWarm, LT.green30, LT.greenMid, LT.greenDeep];
  return (
    <div className="mt-7">
      <h3 className="text-[14.5px] font-medium" style={{ color: LT.greenDeep }}>
        {tr(q.q, locale)}
        <span className="font-normal text-[12.5px] ml-2" style={{ color: LT.green50 }}>{answered.length}</span>
      </h3>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px]" style={{ color: LT.green50 }}>
        {q.levels.map(([lid, l], i) => (
          <span key={lid} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: shades[i], border: `1px solid ${LT.green30}` }} />
            {tr(l, locale)}
          </span>
        ))}
      </div>
      <div className="mt-2 space-y-2">
        {q.rows.map(([rid, rl]) => {
          const counts = q.levels.map(([lid]) => answered.filter(r => r.answers[q.id]?.[rid] === lid).length);
          const total = counts.reduce((a, b) => a + b, 0);
          return (
            <div key={rid} className="text-[13.5px]">
              <div className="truncate" title={tr(rl, locale)} style={{ color: '#3a4f44' }}>{tr(rl, locale)}</div>
              <div className="mt-1 h-3 rounded-full overflow-hidden flex" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
                {total > 0 && counts.map((c, i) => c > 0 && (
                  <span key={i} title={`${tr(q.levels[i][1], locale)} · ${c}`}
                        style={{ width: `${(c / total) * 100}%`, background: shades[i] }} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Person({ r, survey, locale }) {
  const date = new Date(r.updated_at).toLocaleDateString(locale === 'en' ? 'en-GB' : `${locale}-BE`, { day: 'numeric', month: 'short' });
  return (
    <details className="border-t py-3" style={{ borderColor: LT.green30 }}>
      <summary className="cursor-pointer flex items-center justify-between gap-3 text-[14.5px]" style={{ color: LT.greenDeep }}>
        <span className="font-medium">
          {r.full_name}
          {r.departement && <span className="font-normal" style={{ color: LT.green50 }}> · {deptLabel(r.departement, locale)}</span>}
        </span>
        <span className="shrink-0 inline-flex items-center gap-2">
          {r.ai.risk && <AlertTriangle className="h-3.5 w-3.5" style={{ color: '#9a5b0f' }} aria-label={tr(UI.risk, locale)} />}
          <span className="text-[11.5px] rounded-full px-2 py-0.5 font-num" style={{ background: LT.beigeWarm, color: LT.greenDeep }}>
            {tr(LEVELS.find(([id]) => id === r.ai.level)[2], locale)} · {r.ai.score}
          </span>
          <span className="text-[12px] font-num" style={{ color: LT.green50 }}>{date}</span>
        </span>
      </summary>
      <dl className="mt-3 space-y-2 text-[14px]">
        {allQuestions(survey).map(q => {
          const text = answerText(q, r.answers, locale);
          if (!text) return null;
          return (
            <div key={q.id}>
              <dt className="text-[12.5px]" style={{ color: LT.green50 }}>{tr(q.q, locale)}</dt>
              <dd className="m-0 whitespace-pre-line [overflow-wrap:anywhere]" style={{ color: '#3a4f44' }}>{text}</dd>
            </div>
          );
        })}
      </dl>
    </details>
  );
}

function answerText(q, answers, locale) {
  const v = answers[q.id];
  if (q.type === 'text') return v || '';
  if (q.type === 'matrix') {
    return q.rows.filter(([rid]) => v?.[rid])
      .map(([rid, rl]) => `${tr(rl, locale)}: ${optionLabel(q, v[rid], locale)}`).join('\n');
  }
  const vals = (Array.isArray(v) ? v : v ? [v] : []).filter(x => x !== '__other');
  const parts = vals.map(id => optionLabel(q, id, locale));
  const other = answers[`${q.id}_other`];
  if (other) parts.push(`${tr(UI.other, locale).replace('…', '')}: ${other}`);
  return parts.join(', ');
}

function downloadCsv(survey, rows, locale) {
  const qs = allQuestions(survey);
  const cell = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const header = ['Name', 'Email', 'Department', 'Updated', 'AI score', 'AI level', 'Risk flag', ...qs.map(q => tr(q.q, locale))];
  const lines = rows.map(r => [
    r.full_name, r.email, deptLabel(r.departement, locale), r.updated_at,
    r.ai.score, tr(LEVELS.find(([id]) => id === r.ai.level)[2], locale), r.ai.risk ? 'yes' : '',
    ...qs.map(q => answerText(q, r.answers, locale)),
  ]);
  const csv = '﻿' + [header, ...lines].map(l => l.map(cell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: `${survey.slug}.csv` });
  a.click();
  URL.revokeObjectURL(url);
}

